// src/screens/Wallet.jsx
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import "../css/Wallet.css";
import Swal from "sweetalert2";
import * as XLSX from "xlsx";
import Chart from "chart.js/auto";
import useIdleLogout from "../hooks/useIdleLogout"; // ✅ shared idle/logout hook

/* ============== CONFIG (ENV) ============== */
const WALLET_BY_USER_BASE = (process.env.REACT_APP_WALLET_BY_USER_API_URL || "").replace(/\/+$/, "");
const TX_BASE = (process.env.REACT_APP_WALLET_TX_BY_USER_API_URL || "").replace(/\/+$/, ""); // optional, legacy
// Cursor / path-style tx API (supports `/wallet/:walletId` & `/user/:userId` + cursor)
const TX_CURSOR_API = (process.env.REACT_APP_WALLET_TX_CURSOR_API_URL || TX_BASE || "").replace(/\/+$/, "");

// API for admin → user tips deposit
const TIP_DEPOSIT_API = (process.env.REACT_APP_TIP_DEPOSIT_API_URL || "").replace(/\/+$/, "");
const WALLET_TPIN_API = (process.env.REACT_APP_WALLET_TPIN_API_URL || "").replace(/\/+$/, "");

// we only need drivers API for the “Average Rated Drivers” section
const API_DRIVERS = (process.env.REACT_APP_DRIVERS_API_URL || "").trim();
const BASEURL_ADMIN = (process.env.REACT_APP_BASEURL_ADMIN || "").trim();

// optional fallback only; real id comes from localStorage set at login
const DEFAULT_USER_ID = (process.env.REACT_APP_WALLET_USER_ID || "").trim();

/* Actions now ENABLED so deposit works */
const ACTIONS_ENABLED = true;
const FIXED_REWARD_AMOUNT_NU = 200;
const FIXED_REWARD_AMOUNT_TEXT = FIXED_REWARD_AMOUNT_NU.toFixed(2);
const AVG_RATED_PAGE_SIZE = 5;
const AVG_RATED_MAX_PAGES = 100;
const REWARD_COOLDOWN_MONTHS = 6;

/* ============== HELPERS ============== */
const formatBTNNumber = (num) =>
  Number(num || 0).toLocaleString("en-US", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

const fmt = (cents) => `BTN. ${formatBTNNumber(Number(cents || 0) / 100)}`;

/* Escape HTML for safe interpolation inside Swal html */
function escapeHtml(str = "") {
  return String(str)
    .replaceAll("&", "&amp;")
    .replaceAll("<", "&lt;")
    .replaceAll(">", "&gt;")
    .replaceAll('"', "&quot;")
    .replaceAll("'", "&#039;");
}

/* Fingerprint to dedupe tx coming from multiple APIs with different ids/refs */
function txFingerprint(r) {
  const tsSec = Math.floor(Number(r.ts || 0) / 1000); // second precision
  const dir = String(r.dir || "").toUpperCase();
  const amt = Number(r.amt || 0);
  const aw = String(r.admin_wallet_id || "");
  const uw = String(r.user_wallet_id || "");
  const jr = String(r.journal || "");
  return `${tsSec}|${dir}|${amt}|${aw}|${uw}|${jr}`;
}

/* Merge + dedupe helper */
function mergeDedupTx(prev = [], next = []) {
  const map = new Map();
  const pickRicher = (a, b) => {
    const richnessA =
      (a.note ? 1 : 0) +
      (a.ref ? 1 : 0) +
      (a.journal ? 1 : 0) +
      (a.status ? 1 : 0);
    const richnessB =
      (b.note ? 1 : 0) +
      (b.ref ? 1 : 0) +
      (b.journal ? 1 : 0) +
      (b.status ? 1 : 0);
    return richnessB > richnessA ? b : a;
  };
  for (const r of [...prev, ...next]) {
    const k = txFingerprint(r);
    if (!map.has(k)) map.set(k, r);
    else map.set(k, pickRicher(map.get(k), r));
  }
  const arr = Array.from(map.values());
  arr.sort((a, b) => b.ts - a.ts);
  return arr;
}

/* Parse a value into a valid epoch ms, or null if invalid/empty */
function parseTsMs(value) {
  if (value == null || value === "") return null;
  const t = typeof value === "number" ? value : new Date(value).getTime();
  return Number.isFinite(t) ? t : null;
}

/* Legacy/other shape → table rows */
function normalizeTx(arr = []) {
  return arr.map((t, i) => {
    const id = t.id ?? t.tx_id ?? `tx_${i + 1}`;
    const ts =
      parseTsMs(t.ts) ??
      parseTsMs(t.created_at && String(t.created_at).replace(" ", "T")) ??
      Date.now();
    const rawDir = (t.dir || t.direction || "").toString().toUpperCase();
    const dir = rawDir === "CR" || rawDir === "CREDIT" ? "CREDIT" : "DEBIT";
    const status = (t.status ?? t.state ?? "SUCCESS").toString().toUpperCase();
    const amountNu =
      Number(t.amount_nu ?? t.amount ?? t.total ?? 0) ||
      Number(((t.amount_cents ?? 0) / 100).toFixed(2));
    const amtCents = Math.round(amountNu * 100);

    return {
      id,
      ts,
      dir,
      status,
      amt: amtCents,
      journal: t.journal_code || t.journal || t.ref || t.reference || "",
      admin_wallet_id: t.wallet_id || t.admin_wallet_id || "",
      user_wallet_id: t.counterparty_wallet_id || t.user_wallet_id || "",
      note: t.note || t.remarks || t.remark || "",
      ref: t.ref ?? t.reference ?? "",
    };
  });
}

/* Cursor/path endpoint shape → table rows (journal_code etc.) */
function normalizeCursorTx(items = []) {
  return items.map((t, i) => {
    // created_at_local is sometimes malformed (e.g. "24:16:41" from a bad
    // day-rollover when converting to local time), which JS parses as
    // Invalid Date. Fall back to created_at (UTC, well-formed) before
    // giving up to Date.now() — otherwise these rows always look "live"
    // and float to the top of the table on every refresh.
    const ts =
      parseTsMs(
        t.created_at_local && String(t.created_at_local).replace(" ", "T")
      ) ??
      parseTsMs(t.created_at) ??
      Date.now();

    const rawDir = String(t.direction || "").toUpperCase();
    const dir = rawDir === "CR" || rawDir === "CREDIT" ? "CREDIT" : "DEBIT";

    const amountNu = Number(t.amount || 0);
    const amt = Math.round(amountNu * 100);

    const journal = t.journal_code || "";
    const ref = t.transaction_id || t.journal_code || "";

    return {
      id: ref || `ctx_${i + 1}`,
      ts,
      dir,
      amt,
      status: (t.status || "SUCCESS").toString().toUpperCase(),
      journal,
      admin_wallet_id: t.wallet_id || "",
      user_wallet_id: t.counterparty_wallet_id || "",
      note: t.note || t.remarks || t.remark || "",
      ref,
    };
  });
}

/** Read the authed admin id saved by the Login page (dynamic real data). */
function getAuthedUserId() {
  try {
    const uidStr =
      localStorage.getItem("user_id") || localStorage.getItem("admin_id");
    if (uidStr) return uidStr;
    const user = JSON.parse(localStorage.getItem("user") || "null");
    const uid = user?.user_id ?? user?.id ?? user?._id;
    if (uid != null) return String(uid);
  } catch {}
  return DEFAULT_USER_ID || "";
}

/** Get admin meta from Login localStorage (name + token). */
function getAdminMeta() {
  try {
    const user = JSON.parse(localStorage.getItem("user") || "{}");
    const admin_name =
      user?.user_name ??
      user?.full_name ??
      user?.name ??
      user?.username ??
      "Admin";
    const token = localStorage.getItem("access_token") || "";
    return { admin_name: String(admin_name), token };
  } catch {
    return { admin_name: "Admin", token: "" };
  }
}

/* time range helpers (Mon-start week) */
const startOfToday = () => {
  const d = new Date();
  d.setHours(0, 0, 0, 0);
  return d.getTime();
};
const startOfWeek = () => {
  const d = new Date();
  const day = (d.getDay() + 6) % 7; // Monday=0
  d.setDate(d.getDate() - day);
  d.setHours(0, 0, 0, 0);
  return d.getTime();
};
const startOfMonth = () => {
  const d = new Date();
  d.setDate(1);
  d.setHours(0, 0, 0, 0);
  return d.getTime();
};
const startOfYear = () => {
  const d = new Date();
  d.setMonth(0, 1);
  d.setHours(0, 0, 0, 0);
  return d.getTime();
};

/* ====== Driver helpers for ratings section ====== */
const DEFAULT_AVATAR =
  process.env.PUBLIC_URL + "/assets/img/admin profile.jpg";

function resolveImageSrc(src) {
  if (!src) return "";
  const s = String(src).trim();
  if (
    /^https?:\/\//i.test(s) ||
    s.startsWith("data:") ||
    s.startsWith("blob:")
  )
    return s;
  if (!BASEURL_ADMIN) return s;
  const base = BASEURL_ADMIN.replace(/\/+$/, "");
  const path = s.replace(/^\/+/, "");
  return `${base}/${path}`;
}

function Avatar({ src, name }) {
  const resolved = resolveImageSrc(src);
  const finalSrc = resolved || DEFAULT_AVATAR;
  return (
    <img
      className={`avatar${resolved ? "" : " avatar-placeholder"}`}
      src={finalSrc}
      alt={name || "avatar"}
      onError={(e) => {
        if (e.currentTarget.src !== DEFAULT_AVATAR) {
          e.currentTarget.onerror = null;
          e.currentTarget.src = DEFAULT_AVATAR;
        }
      }}
    />
  );
}

function formatRating(v) {
  const n = Number(v);
  if (!Number.isFinite(n) || n <= 0) return "—";
  return n.toFixed(1).replace(/\.0$/, "");
}
function getAverageRating(row) {
  return formatRating(row.avg_rating ?? row.average_rating);
}
function getAverageRatingNum(row) {
  const n = Number(row.avg_rating ?? row.average_rating);
  return Number.isFinite(n) ? n : 0;
}

function getWalletId(r) {
  const direct =
    r.wallet_id ??
    r.walletId ??
    r.walletid ??
    r.user_wallet_id ??
    r.admin_wallet_id;
  if (direct) return String(direct);

  const nested =
    r.wallet?.wallet_id ??
    r.wallet?.id ??
    r.profile?.wallet_id ??
    r.meta?.wallet_id;
  if (nested) return String(nested);

  const alt =
    r.business?.wallet_id ?? r.user?.wallet_id ?? r.account?.wallet_id;
  if (alt) return String(alt);

  return "—";
}

function normalizeWalletKey(value) {
  return String(value || "")
    .trim()
    .toUpperCase();
}

function addMonthsToTimestamp(ts, months) {
  const d = new Date(Number(ts || 0));
  if (!Number.isFinite(d.getTime())) return 0;
  d.setMonth(d.getMonth() + months);
  return d.getTime();
}

function getWalletTPinUrl(walletId) {
  const wid = String(walletId || "").trim();
  if (!WALLET_TPIN_API || !wid) return "";
  return `${WALLET_TPIN_API}/wallet/${encodeURIComponent(wid)}/t-pin`;
}

function getWalletHasTPinUrl(userId) {
  const uid = String(userId || "").trim();
  if (!WALLET_TPIN_API || !uid) return "";
  return `${WALLET_TPIN_API}/wallet/${encodeURIComponent(uid)}/has-tpin`;
}

function getWalletForgotTPinUrl(walletId) {
  const wid = String(walletId || "").trim();
  if (!WALLET_TPIN_API || !wid) return "";
  return `${WALLET_TPIN_API}/wallet/${encodeURIComponent(wid)}/forgot-tpin`;
}

function getWalletForgotTPinVerifyUrl(walletId) {
  const wid = String(walletId || "").trim();
  if (!WALLET_TPIN_API || !wid) return "";
  return `${WALLET_TPIN_API}/wallet/${encodeURIComponent(wid)}/forgot-tpin/verify`;
}

/* ===================== COMPONENT ===================== */
export default function Wallet() {
  // ✅ Use shared idle/session hook (same as Users.jsx)
  useIdleLogout();

  /* -------- current admin id (dynamic) -------- */
  const [currentUserId, setCurrentUserId] = useState(getAuthedUserId());

  useEffect(() => {
    const onStorage = (e) => {
      if (["user", "user_id", "admin_id", "access_token"].includes(e.key)) {
        setCurrentUserId(getAuthedUserId());
      }
    };
    window.addEventListener("storage", onStorage);
    return () => window.removeEventListener("storage", onStorage);
  }, []);

  /* -------- wallet state -------- */
  const [wallet, setWallet] = useState({
    id: null,
    wallet_id: "",
    user_id: null,
    status: "ACTIVE",
    balanceCents: 0,
    totalCreditCents: 0, // ✅ optional from backend
    totalDebitCents: 0, // ✅ optional from backend
    created_at: "",
    updated_at: "",
  });
  const [loading, setLoading] = useState(false);
  const [apiErr, setApiErr] = useState("");

  /* -------- transactions state -------- */
  const [tx, setTx] = useState([]);
  const [txLoading, setTxLoading] = useState(false);
  const [txErr, setTxErr] = useState("");

  /* -------- filters -------- */
  const [q, setQ] = useState("");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");

  /* -------- tips (public user payout) -------- */
  const [tipWalletId, setTipWalletId] = useState("");
  const [tipRemark, setTipRemark] = useState("");
  const [tipBusy, setTipBusy] = useState(false);
  const [tpinBusy, setTpinBusy] = useState(false);
  const [tpinCheckBusy, setTpinCheckBusy] = useState(false);
  const [hasTPin, setHasTPin] = useState(false);
  const [walletUnlocked, setWalletUnlocked] = useState(false);
  const [tpinCheckErr, setTpinCheckErr] = useState("");
  const [tipMsg, setTipMsg] = useState({ kind: "", text: "" });
  const remarkMax = 80;

  /* -------- average rated drivers -------- */
  const [avgRatedRows, setAvgRatedRows] = useState([]);
  const [avgRatedLoading, setAvgRatedLoading] = useState(false);
  const [avgRatedErr, setAvgRatedErr] = useState("");
  const [avgRatedPage, setAvgRatedPage] = useState(1);
  const tpinCheckedWalletRef = useRef("");

  const tipAmountCents = useMemo(
    () => Math.round(FIXED_REWARD_AMOUNT_NU * 100),
    []
  );
  const totalPayoutCents = tipAmountCents;

  const rewardPaymentSummary = useMemo(() => {
    const eligibleWalletIds = new Set(
      avgRatedRows
        .map((row) => normalizeWalletKey(getWalletId(row)))
        .filter((walletKey) => walletKey && walletKey !== "—")
    );

    const buildBucket = () => ({
      totalCents: 0,
      paymentsCount: 0,
      paidWalletIds: new Set(),
      amountByWallet: new Map(),
    });

    const allTime = buildBucket();
    const year = buildBucket();
    const cooldown = {
      lockedWalletIds: new Set(),
      nextEligibleAtByWallet: new Map(),
    };
    const lastPaidAtByWallet = new Map();
    const yearStart = startOfYear();
    const nowTs = Date.now();

    const isSpecialRewardTx = (r, walletKey) => {
      if (!walletKey || walletKey === "—") return false;
      if (String(r.status || "").toUpperCase() === "FAILED") return false;
      if (String(r.dir || "").toUpperCase() !== "DEBIT") return false;

      const noteText = String(r.note || "").toUpperCase();
      return (
        noteText.includes("[SPECIAL_REWARD]") ||
        noteText.includes("SPECIAL REWARD")
      );
    };

    const addToBucket = (bucket, walletKey, amountCents) => {
      bucket.totalCents += amountCents;
      bucket.paymentsCount += 1;
      bucket.paidWalletIds.add(walletKey);
      bucket.amountByWallet.set(
        walletKey,
        Number(bucket.amountByWallet.get(walletKey) || 0) + amountCents
      );
    };

    for (const r of tx) {
      const walletKey = normalizeWalletKey(r.user_wallet_id);
      if (!isSpecialRewardTx(r, walletKey)) continue;

      const amountCents = Math.max(0, Number(r.amt || 0));
      const ts = Number(r.ts || 0);
      if (!(ts > 0)) continue;

      const prevLastTs = Number(lastPaidAtByWallet.get(walletKey) || 0);
      if (ts > prevLastTs) lastPaidAtByWallet.set(walletKey, ts);

      if (eligibleWalletIds.has(walletKey)) {
        addToBucket(allTime, walletKey, amountCents);
        if (ts >= yearStart) addToBucket(year, walletKey, amountCents);
      }
    }

    // Track cooldown for every wallet that has ever been paid the special
    // reward, not just wallets currently in the eligible (avg rating > 4.5)
    // table — otherwise a driver who drops out of the table after being
    // paid could be paid again before the 6 month cooldown ends.
    for (const walletKey of lastPaidAtByWallet.keys()) {
      const lastPaidTs = Number(lastPaidAtByWallet.get(walletKey) || 0);
      const nextEligibleTs =
        lastPaidTs > 0
          ? addMonthsToTimestamp(lastPaidTs, REWARD_COOLDOWN_MONTHS)
          : nowTs;

      cooldown.nextEligibleAtByWallet.set(walletKey, nextEligibleTs);

      if (lastPaidTs > 0 && nextEligibleTs > nowTs) {
        cooldown.lockedWalletIds.add(walletKey);
      }
    }

    return {
      currentCycleLabel: `${REWARD_COOLDOWN_MONTHS} month rule`,
      currentCycleShortLabel: `${REWARD_COOLDOWN_MONTHS} month rule`,
      openingMonthLabel: "Any time",
      isPayoutWindowOpen: true,
      payoutOpenStartTs: nowTs,
      payoutOpenEndTs: nowTs,
      nextPayoutOpenTs: nowTs,
      nextPayoutEndTs: nowTs,
      nextPayoutCycleLabel: `${REWARD_COOLDOWN_MONTHS} month rule`,
      nextPayoutCycleShortLabel: `${REWARD_COOLDOWN_MONTHS} month rule`,
      nextOpeningMonthLabel: "Any time",
      cooldownLabel: `${REWARD_COOLDOWN_MONTHS} month rule`,
      yearLabel: String(new Date().getFullYear()),
      eligibleDriverCount: eligibleWalletIds.size,
      eligibleNowCount: Math.max(
        0,
        eligibleWalletIds.size -
          [...eligibleWalletIds].filter((k) => cooldown.lockedWalletIds.has(k))
            .length
      ),
      currentCycle: allTime,
      year,
      lastPaidAtByWallet,
      lastPaidInCurrentCycleAtByWallet: lastPaidAtByWallet,
      nextEligibleAtByWallet: cooldown.nextEligibleAtByWallet,
      cooldown,
    };
  }, [avgRatedRows, tx]);
  const totalAvgRatedPages = useMemo(() => {
    const pages = Math.ceil(avgRatedRows.length / AVG_RATED_PAGE_SIZE);
    return Math.min(AVG_RATED_MAX_PAGES, Math.max(1, pages || 1));
  }, [avgRatedRows.length]);

  const paginatedAvgRatedRows = useMemo(() => {
    const safePage = Math.min(Math.max(1, avgRatedPage), totalAvgRatedPages);
    const start = (safePage - 1) * AVG_RATED_PAGE_SIZE;
    return avgRatedRows.slice(start, start + AVG_RATED_PAGE_SIZE);
  }, [avgRatedRows, avgRatedPage, totalAvgRatedPages]);

  const avgRatedVisibleCount = useMemo(
    () => Math.min(avgRatedRows.length, AVG_RATED_PAGE_SIZE * AVG_RATED_MAX_PAGES),
    [avgRatedRows.length]
  );

  const selectedRewardMeta = useMemo(() => {
    const walletKey = normalizeWalletKey(tipWalletId);
    if (!walletKey) {
      return {
        walletKey: "",
        existsInEligibleTable: false,
        isCoolingDown: false,
        canBePaidNow: false,
        lastPaidTs: 0,
        lastPaidInCurrentCycleTs: 0,
        nextEligibleTs: 0,
      };
    }

    const existsInEligibleTable = avgRatedRows.some(
      (row) => normalizeWalletKey(getWalletId(row)) === walletKey
    );
    const lastPaidTs = Number(
      rewardPaymentSummary.lastPaidAtByWallet.get(walletKey) || 0
    );
    const nextEligibleTs = Number(
      rewardPaymentSummary.nextEligibleAtByWallet.get(walletKey) || 0
    );
    const isCoolingDown =
      rewardPaymentSummary.cooldown.lockedWalletIds.has(walletKey);

    return {
      walletKey,
      existsInEligibleTable,
      isCoolingDown,
      canBePaidNow: !isCoolingDown,
      lastPaidTs,
      lastPaidInCurrentCycleTs: lastPaidTs,
      nextEligibleTs,
    };
  }, [tipWalletId, avgRatedRows, rewardPaymentSummary]);

  useEffect(() => {
    setAvgRatedPage(1);
  }, [avgRatedRows.length]);

  // Inflow (credits) today / week / month / year – in CENTS (like wallet.balanceCents)
  const inflow = useMemo(() => {
    const sumSince = (since) =>
      tx.reduce((sum, r) => {
        if (
          r.dir === "CREDIT" &&
          r.status !== "FAILED" &&
          r.ts >= since
        ) {
          return sum + (r.amt || 0);
        }
        return sum;
      }, 0);
    return {
      today: sumSince(startOfToday()),
      week: sumSince(startOfWeek()),
      month: sumSince(startOfMonth()),
      year: sumSince(startOfYear()),
    };
  }, [tx]);

  /* ============== DATA LOADERS ============== */

  // Legacy/by-user loader (kept as fallback)
  const loadTransactions = useCallback(async (userId, walletId) => {
    if (!TX_BASE) {
      setTxErr("");
      return;
    }
    setTxLoading(true);
    setTxErr("");
    const { token } = getAdminMeta();
    const auth = token ? { Authorization: `Bearer ${token}` } : {};
    try {
      let res = await fetch(`${TX_BASE}/${encodeURIComponent(userId)}`, {
        headers: { Accept: "application/json", ...auth },
      });

      if (!res.ok) {
        res = await fetch(
          `${TX_BASE}?user_id=${encodeURIComponent(userId)}`,
          {
            headers: { Accept: "application/json", ...auth },
          }
        );
      }
      if (!res.ok && walletId) {
        res = await fetch(
          `${TX_BASE}?wallet_id=${encodeURIComponent(walletId)}`,
          {
            headers: { Accept: "application/json", ...auth },
          }
        );
      }
      if (!res.ok) throw new Error(`HTTP ${res.status}`);

      const json = await res.json();
      const arr =
        (Array.isArray(json) && json) ||
        (Array.isArray(json?.data) && json.data) ||
        (Array.isArray(json?.items) && json.items) ||
        (Array.isArray(json?.results) && json.results) ||
        [];
      const mapped = normalizeTx(arr);
      setTx((prev) => mergeDedupTx(prev, mapped));
    } catch (e) {
      setTxErr(`Failed to load transactions: ${e.message}`);
    } finally {
      setTxLoading(false);
    }
  }, []);

  // First page from cursor API – PATH style first
  const loadCursorFirstPage = useCallback(
    async (userId, walletId) => {
      if (!TX_CURSOR_API) return;

      const { token } = getAdminMeta();
      const auth = token ? { Authorization: `Bearer ${token}` } : {};

      // try /wallet/:walletId → then /user/:userId → then bare
      const tries = [];
      if (walletId)
        tries.push(
          `${TX_CURSOR_API}/wallet/${encodeURIComponent(walletId)}`
        );
      if (userId)
        tries.push(
          `${TX_CURSOR_API}/user/${encodeURIComponent(userId)}`
        );
      tries.push(TX_CURSOR_API);

      let ok = false,
        json = null;
      for (const url of tries) {
        try {
          const res = await fetch(url, {
            headers: { Accept: "application/json", ...auth },
          });
            if (!res.ok) throw new Error(`HTTP ${res.status}`);
            const j = await res.json();
            // some endpoints can 200 with an unexpected shape; keep
            // trying the other URLs in that case instead of stopping here
            if (!Array.isArray(j?.data)) throw new Error("Unexpected response shape");
            json = j;
            ok = true;
            break;
        } catch {
          // try next
        }
      }
      if (!ok || !json) return;

      const items = json.data;
      const mapped = normalizeCursorTx(items);
      setTx((prev) => mergeDedupTx(prev, mapped));
      // no cursor footer (per your request)
    },
    []
  );

  const loadWallet = useCallback(
    async (userId) => {
      if (!WALLET_BY_USER_BASE || !userId) {
        setApiErr("Missing wallet API URL or user id.");
        setWallet((w) => ({
          ...w,
          balanceCents: 0,
          totalCreditCents: 0,
          totalDebitCents: 0,
          wallet_id: "",
          user_id: null,
        }));
        setTx([]);
        return;
      }
      setLoading(true);
      setApiErr("");
      const { token } = getAdminMeta();
      const auth = token ? { Authorization: `Bearer ${token}` } : {};
      try {
        const res = await fetch(
          `${WALLET_BY_USER_BASE}/${encodeURIComponent(userId)}`,
          {
            headers: { Accept: "application/json", ...auth },
          }
        );
        if (!res.ok) throw new Error(`HTTP ${res.status}`);

        const json = await res.json();
        const d = json?.data || {};
        const amountNu = Number(d.amount ?? 0);
        const totalCreditNu = Number(
          d.total_credit ?? d.total_credited ?? 0
        );
        const totalDebitNu = Number(
          d.total_debit ?? d.total_debited ?? 0
        );

        setWallet({
          id: d.id ?? null,
          wallet_id: d.wallet_id ?? "",
          user_id: d.user_id ?? null,
          status: d.status ?? "ACTIVE",
          balanceCents: Math.round(amountNu * 100),
          totalCreditCents: Math.round(totalCreditNu * 100),
          totalDebitCents: Math.round(totalDebitNu * 100),
          created_at: d.created_at ?? "",
          updated_at: d.updated_at ?? "",
        });

        // fresh tx for this admin
        setTx([]);
        await loadCursorFirstPage(d.user_id, d.wallet_id);
        await loadTransactions(d.user_id, d.wallet_id); // legacy extra if available
      } catch (e) {
        setApiErr(`Failed to load wallet: ${e.message}`);
        setTx([]);
      } finally {
        setLoading(false);
      }
    },
    [loadTransactions, loadCursorFirstPage]
  );

  useEffect(() => {
    loadWallet(currentUserId);
  }, [currentUserId, loadWallet]);

  /* ============== LOAD AVERAGE RATED DRIVERS ONLY ============== */
  const loadAvgRatedUsers = useCallback(async () => {
    if (!API_DRIVERS) {
      setAvgRatedErr("Missing REACT_APP_DRIVERS_API_URL");
      setAvgRatedRows([]);
      return;
    }

    setAvgRatedLoading(true);
    setAvgRatedErr("");
    const { token } = getAdminMeta();
    const auth = token ? { Authorization: `Bearer ${token}` } : {};

    try {
      const dRes = await fetch(API_DRIVERS, {
        headers: { Accept: "application/json", ...auth },
      });

      if (!dRes.ok) throw new Error(`Drivers HTTP ${dRes.status}`);

      const dJson = await dRes.json();

      const driversArr = Array.isArray(dJson)
        ? dJson
        : Array.isArray(dJson?.data)
        ? dJson.data
        : [];

      const mappedDrivers = driversArr.map((r) => {
        const v = (r.vehicles && r.vehicles[0]) || {};
        const veh = [v.make, v.color, v.license_plate,v.vehicle_type]
          .filter(Boolean)
          .join(" • ");
        return {
          ...r,
          kind: "Driver",
          extra: veh || r.license_number || "",
        };
      });

      // only drivers with avg rating > 4.5
      const topDrivers = mappedDrivers
        .filter((r) => getAverageRatingNum(r) > 4.5)
        .sort((a, b) => getAverageRatingNum(b) - getAverageRatingNum(a));

      setAvgRatedRows(topDrivers);
    } catch (e) {
      setAvgRatedErr(e.message || "Failed to load rated drivers");
      setAvgRatedRows([]);
    } finally {
      setAvgRatedLoading(false);
    }
  }, []);

  useEffect(() => {
    loadAvgRatedUsers();
  }, [loadAvgRatedUsers]);

  async function promptCreateTPin(walletId, options = {}) {
    const { token } = getAdminMeta();
    const createUrl = getWalletTPinUrl(walletId);
    const title = options.title || "Create T-PIN";
    const helperText =
      options.helperText ||
      "Please create your new 4 digit T-PIN to continue.";

    if (!walletId) {
      await Swal.fire({ icon: "error", title: "Missing wallet ID" });
      return false;
    }
    if (!WALLET_TPIN_API || !createUrl) {
      await Swal.fire({ icon: "error", title: "Missing wallet T-PIN API URL" });
      return false;
    }

    const pinPrompt = await Swal.fire({
      title,
      html: `
        <div style="font-size:13px; color:#4b5563; margin-bottom:10px;">${escapeHtml(helperText)}</div>
        <input id="swal-create-pin" class="swal2-input" type="password" inputmode="numeric" maxlength="4" placeholder="New 4 digit T-PIN" />
        <input id="swal-create-pin-confirm" class="swal2-input" type="password" inputmode="numeric" maxlength="4" placeholder="Confirm new T-PIN" />
      `,
      allowOutsideClick: false,
      allowEscapeKey: !!options.allowCancel,
      showCancelButton: options.allowCancel !== false,
      confirmButtonText: "Save T-PIN",
      cancelButtonText: "Cancel",
      reverseButtons: true,
      focusConfirm: false,
      preConfirm: () => {
        const pin = String(document.getElementById("swal-create-pin")?.value || "").trim();
        const confirmPin = String(document.getElementById("swal-create-pin-confirm")?.value || "").trim();
        if (!/^\d{4}$/.test(pin)) {
          Swal.showValidationMessage("Please enter a valid 4 digit T-PIN.");
          return false;
        }
        if (pin !== confirmPin) {
          Swal.showValidationMessage("T-PIN and confirm T-PIN do not match.");
          return false;
        }
        return pin;
      },
    });

    if (!pinPrompt.isConfirmed) return false;

    const nextPin = String(pinPrompt.value || "").trim();

    const res = await fetch(createUrl, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
      body: JSON.stringify({ t_pin: nextPin }),
    });

    if (!res.ok) {
      let msg = `T-PIN creation failed (HTTP ${res.status})`;
      try {
        const j = await res.json();
        msg = j?.message || j?.error || msg;
      } catch {}
      throw new Error(msg);
    }

    await Swal.fire({
      icon: "success",
      title: "T-PIN created successfully",
      timer: 1600,
      showConfirmButton: false,
    });
    return true;
  }

  const checkWalletTPinStatus = useCallback(
    async (userId, walletId, options = {}) => {
      const uid = String(userId || "").trim();
      const wid = String(walletId || "").trim();
      const checkKey = `${uid}|${wid}`;
      const checkUrl = getWalletHasTPinUrl(uid);
      const { token } = getAdminMeta();
      const auth = token ? { Authorization: `Bearer ${token}` } : {};
      const shouldShowLoader = options.showLoader !== false;
      const shouldShowErrorAlert = options.showErrorAlert !== false;

      if (!uid || !wid) return false;
      if (!options.force && tpinCheckedWalletRef.current === checkKey && walletUnlocked) {
        return true;
      }
      if (!WALLET_TPIN_API || !checkUrl) {
        const msg = "Missing wallet T-PIN API URL.";
        setWalletUnlocked(false);
        setHasTPin(false);
        setTpinCheckErr(msg);
        if (shouldShowErrorAlert) {
          await Swal.fire({ icon: "error", title: "Wallet T-PIN unavailable", text: msg });
        }
        return false;
      }

      setTpinCheckBusy(true);
      setTpinCheckErr("");
      setWalletUnlocked(false);

      if (shouldShowLoader) {
        Swal.fire({
          title: "Checking wallet T-PIN",
          text: "Please wait while we verify wallet security.",
          allowOutsideClick: false,
          allowEscapeKey: false,
          didOpen: () => {
            Swal.showLoading();
          },
        });
      }

      try {
        let json = null;
        let shouldPromptCreate = false;

        try {
          const res = await fetch(checkUrl, {
            headers: { Accept: "application/json", ...auth },
          });
          try {
            json = await res.json();
          } catch {
            json = null;
          }

          const hasTpinFlag =
            json?.success === true ||
            json?.has_tpin === true ||
            json?.data?.has_tpin === true ||
            json?.exists === true ||
            json?.data?.exists === true;

          if (res.ok && hasTpinFlag) {
            if (shouldShowLoader) Swal.close();
            setHasTPin(true);
            setWalletUnlocked(true);
            setTpinCheckErr("");
            tpinCheckedWalletRef.current = checkKey;
            return true;
          }

          const noTpinFlag =
            json?.success === false ||
            json?.has_tpin === false ||
            json?.data?.has_tpin === false ||
            json?.exists === false ||
            json?.data?.exists === false;

          if (res.status === 404 || noTpinFlag || res.ok) {
            shouldPromptCreate = true;
          } else {
            const msg = json?.message || json?.error || `T-PIN check failed (HTTP ${res.status})`;
            throw new Error(msg);
          }
        } catch (err) {
          if (!shouldPromptCreate) throw err;
        }

        if (shouldShowLoader) Swal.close();
        setHasTPin(false);
        const created = await promptCreateTPin(wid, {
          title: "Create T-PIN",
          helperText: "Your wallet does not have a T-PIN yet. Please create one to open the wallet.",
          allowCancel: true,
        });

        if (!created) {
          const msg = "T-PIN setup is required before opening the wallet.";
          setWalletUnlocked(false);
          setHasTPin(false);
          setTpinCheckErr(msg);
          if (shouldShowErrorAlert) {
            await Swal.fire({ icon: "warning", title: "T-PIN required", text: msg });
          }
          return false;
        }

        setHasTPin(true);
        setWalletUnlocked(true);
        setTpinCheckErr("");
        tpinCheckedWalletRef.current = checkKey;
        return true;
      } catch (err) {
        if (shouldShowLoader) Swal.close();
        const msg = err?.message || "Failed to verify wallet T-PIN status.";
        setWalletUnlocked(false);
        setHasTPin(false);
        setTpinCheckErr(msg);
        if (shouldShowErrorAlert) {
          await Swal.fire({ icon: "error", title: "Wallet T-PIN check failed", text: msg });
        }
        return false;
      } finally {
        setTpinCheckBusy(false);
      }
    },
    [walletUnlocked]
  );

  useEffect(() => {
    if (!currentUserId || !wallet.wallet_id) {
      setWalletUnlocked(false);
      setHasTPin(false);
      return;
    }
    checkWalletTPinStatus(currentUserId, wallet.wallet_id, { showLoader: true, showErrorAlert: true });
  }, [currentUserId, wallet.wallet_id, checkWalletTPinStatus]);

  async function handleChangeTPin() {
    if (tipBusy || tpinBusy) return;

    const admin_wallet_id = String(wallet.wallet_id || "").trim();
    const { token } = getAdminMeta();
    const changeUrl = getWalletTPinUrl(admin_wallet_id);

    if (!admin_wallet_id) {
      await Swal.fire({ icon: "error", title: "Missing admin wallet ID" });
      return;
    }
    if (!WALLET_TPIN_API || !changeUrl) {
      await Swal.fire({ icon: "error", title: "Missing wallet T-PIN API URL" });
      return;
    }

    const pinPrompt = await Swal.fire({
      title: "Change T-PIN",
      html: `
        <div style="font-size:13px; color:#4b5563; margin-bottom:10px;">Wallet: <code>${escapeHtml(admin_wallet_id)}</code></div>
        <input id="swal-old-pin" class="swal2-input" type="password" inputmode="numeric" maxlength="4" placeholder="Current 4 digit T-PIN" />
        <input id="swal-new-pin" class="swal2-input" type="password" inputmode="numeric" maxlength="4" placeholder="New 4 digit T-PIN" />
        <input id="swal-new-pin-confirm" class="swal2-input" type="password" inputmode="numeric" maxlength="4" placeholder="Confirm new T-PIN" />
      `,
      showCancelButton: true,
      confirmButtonText: "Update T-PIN",
      cancelButtonText: "Cancel",
      reverseButtons: true,
      focusConfirm: false,
      preConfirm: () => {
        const oldPin = String(document.getElementById("swal-old-pin")?.value || "").trim();
        const newPin = String(document.getElementById("swal-new-pin")?.value || "").trim();
        const confirmPin = String(document.getElementById("swal-new-pin-confirm")?.value || "").trim();
        if (!/^\d{4}$/.test(oldPin)) {
          Swal.showValidationMessage("Please enter your current 4 digit T-PIN.");
          return false;
        }
        if (!/^\d{4}$/.test(newPin)) {
          Swal.showValidationMessage("Please enter a valid new 4 digit T-PIN.");
          return false;
        }
        if (newPin !== confirmPin) {
          Swal.showValidationMessage("New T-PIN and confirm T-PIN do not match.");
          return false;
        }
        if (oldPin === newPin) {
          Swal.showValidationMessage("New T-PIN must be different from current T-PIN.");
          return false;
        }
        return { oldPin, newPin };
      },
    });

    if (!pinPrompt.isConfirmed) return;

    const { oldPin, newPin } = pinPrompt.value || {};
    setTpinBusy(true);
    try {
      const res = await fetch(changeUrl, {
        method: "PATCH",
        headers: {
          "Content-Type": "application/json",
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify({ old_t_pin: oldPin, new_t_pin: newPin }),
      });

      if (!res.ok) {
        let msg = `T-PIN change failed (HTTP ${res.status})`;
        try {
          const j = await res.json();
          msg = j?.message || j?.error || msg;
        } catch {}
        throw new Error(msg);
      }

      setHasTPin(true);
      await Swal.fire({
        icon: "success",
        title: "T-PIN updated successfully",
        timer: 1600,
        showConfirmButton: false,
      });
    } catch (err) {
      await Swal.fire({
        icon: "error",
        title: "T-PIN change failed",
        text: err.message || "Something went wrong.",
      });
    } finally {
      setTpinBusy(false);
    }
  }

  async function handleForgotTPin() {
    if (tipBusy || tpinBusy) return;

    const admin_wallet_id = String(wallet.wallet_id || "").trim();
    const { token } = getAdminMeta();
    const forgotUrl = getWalletForgotTPinUrl(admin_wallet_id);
    const verifyUrl = getWalletForgotTPinVerifyUrl(admin_wallet_id);

    if (!admin_wallet_id) {
      await Swal.fire({ icon: "error", title: "Missing admin wallet ID" });
      return;
    }
    if (!WALLET_TPIN_API || !forgotUrl || !verifyUrl) {
      await Swal.fire({ icon: "error", title: "Missing wallet T-PIN API URL" });
      return;
    }

    setTpinBusy(true);
    try {
      const sendRes = await fetch(forgotUrl, {
        method: "POST",
        headers: {
          Accept: "application/json",
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
      });

      if (!sendRes.ok) {
        let msg = `Failed to send OTP (HTTP ${sendRes.status})`;
        try {
          const j = await sendRes.json();
          msg = j?.message || j?.error || msg;
        } catch {}
        throw new Error(msg);
      }

      const verifyPrompt = await Swal.fire({
        title: "Verify OTP & Set New T-PIN",
        html: `
          <div style="font-size:13px; color:#4b5563; margin-bottom:10px;">An OTP has been sent to the registered email for <code>${escapeHtml(admin_wallet_id)}</code>.</div>
          <input id="swal-forgot-otp" class="swal2-input" type="text" inputmode="numeric" maxlength="6" placeholder="Enter OTP" />
          <input id="swal-forgot-new-pin" class="swal2-input" type="password" inputmode="numeric" maxlength="4" placeholder="New 4 digit T-PIN" />
          <input id="swal-forgot-new-pin-confirm" class="swal2-input" type="password" inputmode="numeric" maxlength="4" placeholder="Confirm new T-PIN" />
        `,
        showCancelButton: true,
        confirmButtonText: "Verify & Save",
        cancelButtonText: "Cancel",
        reverseButtons: true,
        focusConfirm: false,
        preConfirm: () => {
          const otp = String(document.getElementById("swal-forgot-otp")?.value || "").trim();
          const newPin = String(document.getElementById("swal-forgot-new-pin")?.value || "").trim();
          const confirmPin = String(document.getElementById("swal-forgot-new-pin-confirm")?.value || "").trim();
          if (!/^\d{4,8}$/.test(otp)) {
            Swal.showValidationMessage("Please enter a valid OTP.");
            return false;
          }
          if (!/^\d{4}$/.test(newPin)) {
            Swal.showValidationMessage("Please enter a valid new 4 digit T-PIN.");
            return false;
          }
          if (newPin !== confirmPin) {
            Swal.showValidationMessage("New T-PIN and confirm T-PIN do not match.");
            return false;
          }
          return { otp, newPin };
        },
      });

      if (!verifyPrompt.isConfirmed) return;

      const { otp, newPin } = verifyPrompt.value || {};
      const verifyRes = await fetch(verifyUrl, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify({ otp, new_t_pin: newPin }),
      });

      if (!verifyRes.ok) {
        let msg = `OTP verification failed (HTTP ${verifyRes.status})`;
        try {
          const j = await verifyRes.json();
          msg = j?.message || j?.error || msg;
        } catch {}
        throw new Error(msg);
      }

      setHasTPin(true);
      setWalletUnlocked(true);
      await Swal.fire({
        icon: "success",
        title: "T-PIN reset successfully",
        timer: 1600,
        showConfirmButton: false,
      });
    } catch (err) {
      await Swal.fire({
        icon: "error",
        title: "Forgot T-PIN failed",
        text: err.message || "Something went wrong.",
      });
    } finally {
      setTpinBusy(false);
    }
  }

  async function promptForDepositTPin() {
    while (true) {
      let modalAction = "";
      const pinPrompt = await Swal.fire({
        title: "Enter T-PIN",
        html: `
          <div style="font-size:13px; color:#4b5563; margin-bottom:10px;">Please enter your 4 digit T-PIN to continue.</div>
          <input id="swal-deposit-pin" class="swal2-input" type="password" inputmode="numeric" maxlength="4" placeholder="••••" />
          <div style="margin-top:8px; display:flex; justify-content:center; gap:10px; flex-wrap:wrap;">
            <button type="button" id="swal-change-pin-btn" class="btn btn-outline-secondary" style="border-radius:999px; padding:8px 14px;">Change T-PIN</button>
            <button type="button" id="swal-forgot-pin-btn" class="btn btn-outline-secondary" style="border-radius:999px; padding:8px 14px;">Forgot T-PIN</button>
          </div>
        `,
        showCancelButton: true,
        confirmButtonText: "Continue",
        cancelButtonText: "Cancel",
        reverseButtons: true,
        focusConfirm: false,
        didOpen: () => {
          const input = document.getElementById("swal-deposit-pin");
          if (input) input.focus();

          const changeBtn = document.getElementById("swal-change-pin-btn");
          const forgotBtn = document.getElementById("swal-forgot-pin-btn");

          if (changeBtn) {
            changeBtn.addEventListener("click", () => {
              modalAction = "change";
              Swal.close();
            });
          }
          if (forgotBtn) {
            forgotBtn.addEventListener("click", () => {
              modalAction = "forgot";
              Swal.close();
            });
          }
        },
        preConfirm: () => {
          const pin = String(document.getElementById("swal-deposit-pin")?.value || "").trim();
          if (!/^\d{4}$/.test(pin)) {
            Swal.showValidationMessage("Please enter a valid 4 digit T-PIN.");
            return false;
          }
          return pin;
        },
      });

      if (modalAction === "change") {
        await handleChangeTPin();
        continue;
      }
      if (modalAction === "forgot") {
        await handleForgotTPin();
        continue;
      }
      if (!pinPrompt.isConfirmed) return "";
      return String(pinPrompt.value || "").trim();
    }
  }

  /* ============== TIP DEPOSIT HANDLER (with confirmation) ============== */
  async function handleDepositTips() {
    if (!ACTIONS_ENABLED || tipBusy) return;
    setTipMsg({ kind: "", text: "" });

    const amount = FIXED_REWARD_AMOUNT_NU;
    const user_wallet_id = tipWalletId.trim();
    const admin_wallet_id = wallet.wallet_id || "";
    const { admin_name, token } = getAdminMeta();

    if (!walletUnlocked || !hasTPin) {
      setTipMsg({ kind: "err", text: "Set up wallet T-PIN first." });
      await Swal.fire({ icon: "warning", title: "Set up wallet T-PIN first" });
      return;
    }
    if (!admin_wallet_id) {
      setTipMsg({ kind: "err", text: "Missing admin wallet ID." });
      Swal.fire({ icon: "error", title: "Missing admin wallet ID" });
      return;
    }
    if (!user_wallet_id) {
      setTipMsg({ kind: "err", text: "Enter recipient wallet ID." });
      Swal.fire({
        icon: "warning",
        title: "Recipient wallet ID is required",
      });
      return;
    }
    if (!(amount > 0)) {
      setTipMsg({ kind: "err", text: "Enter a valid amount." });
      Swal.fire({ icon: "warning", title: "Enter a valid amount" });
      return;
    }
    if (!TIP_DEPOSIT_API) {
      setTipMsg({ kind: "err", text: "Missing tip deposit API URL." });
      Swal.fire({ icon: "error", title: "Missing tip deposit API URL" });
      return;
    }

    const recipientWalletKey = normalizeWalletKey(user_wallet_id);
    const lastPaidTs = Number(
      rewardPaymentSummary.lastPaidAtByWallet.get(recipientWalletKey) || 0
    );
    const nextEligibleTs = Number(
      rewardPaymentSummary.nextEligibleAtByWallet.get(recipientWalletKey) || 0
    );
    const isCoolingDown =
      rewardPaymentSummary.cooldown.lockedWalletIds.has(recipientWalletKey);

    if (isCoolingDown) {
      const msg = `This user already received the special reward on ${new Date(
        lastPaidTs
      ).toLocaleString()}. Reward can be paid again only after ${new Date(
        nextEligibleTs
      ).toLocaleString()}.`;
      setTipMsg({ kind: "err", text: msg });
      await Swal.fire({
        icon: "warning",
        title: "Reward already paid",
        text: msg,
      });
      return;
    }

    const t_pin = await promptForDepositTPin();
    if (!t_pin) return;

    const amountHtml = `<strong>Nu. ${formatBTNNumber(amount)}</strong>`;
    const html = `
      You are about to transfer ${amountHtml}.
      <div style="margin-top:8px; font-size:12px; line-height:1.5;">
        <div><b>From (Admin Wallet):</b> <code>${escapeHtml(
          admin_wallet_id
        )}</code></div>
        <div><b>To (User Wallet):</b> <code>${escapeHtml(
          user_wallet_id
        )}</code></div>
        <div><b>Admin:</b> ${escapeHtml(admin_name)}</div>
        <div><b>Remark:</b> ${
          tipRemark ? escapeHtml(tipRemark) : "<em>none</em>"
        }</div>
      </div>
      <div style="margin-top:10px; font-size:12px; color:#6b7280;">Please confirm to proceed.</div>
    `;

    const confirm = await Swal.fire({
      icon: "question",
      title: "Confirm tip deposit",
      html,
      showCancelButton: true,
      confirmButtonText: "Yes, deposit",
      cancelButtonText: "Cancel",
      reverseButtons: true,
      focusCancel: true,
    });

    if (!confirm.isConfirmed) return;

    setTipBusy(true);
    try {
      const rewardNote = tipRemark
        ? `[SPECIAL_REWARD] ${tipRemark}`
        : "[SPECIAL_REWARD]";

      const payload = {
        admin_name,
        admin_wallet_id,
        user_wallet_id,
        amount,
        note: rewardNote,
        t_pin,
      };

      const res = await fetch(TIP_DEPOSIT_API, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify(payload),
      });

      if (!res.ok) {
        let msg = `Tip deposit failed (HTTP ${res.status})`;
        try {
          const j = await res.json();
          msg = j?.message || j?.error || msg;
        } catch {}
        throw new Error(msg);
      }

      setTipMsg({ kind: "ok", text: "Tips deposited successfully." });
      await Swal.fire({
        icon: "success",
        title: "Tips deposited successfully",
        timer: 1600,
        showConfirmButton: false,
      });

      setTipWalletId("");
      setTipRemark("");

      // refresh data
      try {
        await loadCursorFirstPage(wallet.user_id, wallet.wallet_id);
      } catch {}
      try {
        await loadTransactions(wallet.user_id, wallet.wallet_id);
      } catch {}
      try {
        await loadWallet(currentUserId);
      } catch {}
      try {
        await loadAvgRatedUsers();
      } catch {}
    } catch (err) {
      setTipMsg({
        kind: "err",
        text: err.message || "Something went wrong.",
      });
      await Swal.fire({
        icon: "error",
        title: "Deposit failed",
        text: err.message || "Something went wrong.",
      });
    } finally {
      setTipBusy(false);
    }
  }

  /* ============== FILTERED TABLE ROWS (sorted desc) ============== */
  const rows = useMemo(() => {
    const s = q.trim().toLowerCase();
    const startTs = startDate
      ? new Date(startDate).setHours(0, 0, 0, 0)
      : null;
    const endTs = endDate
      ? new Date(endDate).setHours(23, 59, 59, 999)
      : null;

    return tx
      .slice()
      .sort((a, b) => b.ts - a.ts)
      .filter((r) => {
        const okQ =
          !s ||
          (r.journal || "").toLowerCase().includes(s) ||
          (r.ref || "").toLowerCase().includes(s) ||
          (r.note || "").toLowerCase().includes(s) ||
          (r.admin_wallet_id || "").toLowerCase().includes(s) ||
          (r.user_wallet_id || "").toLowerCase().includes(s);
        const okStart = !startTs || r.ts >= startTs;
        const okEnd = !endTs || r.ts <= endTs;
        return okQ && okStart && okEnd;
      });
  }, [tx, q, startDate, endDate]);

  /* Only show the latest 10 rows in the table (export still uses all filtered rows) */
  const displayRows = useMemo(() => rows.slice(0, 10), [rows]);

  /* ============== EXPORT: EXCEL ONLY (all filtered rows, deduped) ============== */
  function exportExcel() {
    const deduped = mergeDedupTx([], rows);
    if (!deduped.length) {
      Swal.fire({
        icon: "info",
        title: "No data",
        text: "Nothing to export.",
      });
      return;
    }

    // data rows (header row will be at row 4)
    const data = deduped.map((r, idx) => ({
      "SI.No": idx + 1,
      Date: new Date(r.ts).toLocaleString(),
      "Journal number": r.journal || "",
      "Admin Wallet ID": r.admin_wallet_id || "",
      "Users Wallet ID": r.user_wallet_id || "",
      Direction: r.dir,
      "Amount (BTN.)": formatBTNNumber(r.amt / 100),
      Reference: r.ref || "",
      Remarks: r.note || "",
      Status: r.status,
    }));

    const ws = XLSX.utils.json_to_sheet(data, { origin: "A4" });

    XLSX.utils.sheet_add_aoa(
      ws,
      [
        ["Wallet Transactions"],
        [`Admin Wallet ID: ${wallet.wallet_id || "—"}`],
        [`Exported: ${new Date().toLocaleString()}`],
      ],
      { origin: "A1" }
    );

    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Transactions");
    XLSX.writeFile(wb, "wallet-transactions.xlsx");
  }

  /* ---------- fixed column widths ---------- */
  const tableStyle = { tableLayout: "fixed", width: "100%" };
  const monoEllip = {
    fontFamily:
      "ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, 'Liberation Mono', 'Courier New', monospace",
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
  };
  const remarksStyle = {
    whiteSpace: "normal",
    overflowWrap: "anywhere",
    wordBreak: "break-word",
  };

  /* ============== ANALYTICS (charts data) ============== */
  const chartsRef = useRef({}); // <-- single declaration only

  const analytics = useMemo(() => {
    const ok = (r) => r && r.status !== "FAILED";
    const byDay = new Map(); // key: YYYY-MM-DD -> {cr, db, net}
    const add = (ts, dir, amt) => {
      const d = new Date(ts);
      const key = d.toISOString().slice(0, 10);
      if (!byDay.has(key)) byDay.set(key, { cr: 0, db: 0, net: 0 });
      const obj = byDay.get(key);
      if (dir === "CREDIT") obj.cr += amt;
      else obj.db += amt;
      obj.net += dir === "CREDIT" ? amt : -amt;
    };

    for (const r of tx) if (ok(r)) add(r.ts, r.dir, r.amt);

    const lastNDays = (n) => {
      const labels = [];
      const cr = [];
      const db = [];
      const net = [];
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      for (let i = n - 1; i >= 0; i--) {
        const d = new Date(today);
        d.setDate(today.getDate() - i);
        const key = d.toISOString().slice(0, 10);
        const x = byDay.get(key) || { cr: 0, db: 0, net: 0 };
        labels.push(d.toLocaleDateString());
        cr.push(x.cr / 100);
        db.push(x.db / 100);
        net.push(x.net / 100);
      }
      return { labels, cr, db, net };
    };

    const last30 = lastNDays(30);
    const balances = new Array(30);
    let balCents = wallet.balanceCents;
    for (let i = 29; i >= 0; i--) {
      balances[i] = balCents / 100;
      balCents -= Math.round(last30.net[i] * 100);
    }

    const sumSince = (since, dir) =>
      tx.reduce((sum, r) => {
        if (
          r.ts >= since &&
          r.status !== "FAILED" &&
          (!dir || r.dir === dir)
        ) {
          return sum + (r.amt || 0);
        }
        return sum;
      }, 0) / 100;

    const totals = {
      today: {
        cr: sumSince(startOfToday(), "CREDIT"),
        db: sumSince(startOfToday(), "DEBIT"),
      },
      week: {
        cr: sumSince(startOfWeek(), "CREDIT"),
        db: sumSince(startOfWeek(), "DEBIT"),
      },
      month: {
        cr: sumSince(startOfMonth(), "CREDIT"),
        db: sumSince(startOfMonth(), "DEBIT"),
      },
      year: {
        cr: sumSince(startOfYear(), "CREDIT"),
        db: sumSince(startOfYear(), "DEBIT"),
      },
      all: { cr: sumSince(0, "CREDIT"), db: sumSince(0, "DEBIT") },
    };

    const filtered = rows.reduce(
      (acc, r) => {
        if (r.status === "FAILED") return acc;
        if (r.dir === "CREDIT") acc.cr += r.amt;
        else acc.db += r.amt;
        return acc;
      },
      { cr: 0, db: 0 }
    );
    const filteredBTN = { cr: filtered.cr / 100, db: filtered.db / 100 };

    return {
      daily14: lastNDays(14),
      balance30: { labels: last30.labels, balances },
      totals,
      filteredBTN,
    };
  }, [tx, wallet.balanceCents, rows]);

  // For summary row: use DB totals if backend sent them, else fall back to analytics
  const totalCredBTN =
    wallet.totalCreditCents > 0
      ? wallet.totalCreditCents / 100
      : analytics.totals.all.cr || 0;
  const totalDebitBTN =
    wallet.totalDebitCents > 0
      ? wallet.totalDebitCents / 100
      : analytics.totals.all.db || 0;

  // ======= CREDIT/DEBIT PULSE =======
  const [pulse, setPulse] = useState({
    crCents: 0,
    dbCents: 0,
    visible: false,
  });
  const seenRef = useRef(new Set());
  const hideTimerRef = useRef(null);
  const pulseInitRef = useRef(false);

  useEffect(() => {
    if (!tx.length) return;

    // First time tx loads (e.g. on page refresh), just record what's
    // already there without pulsing — only pulse for tx that arrive
    // afterwards (e.g. after a deposit) so the badge shows once, not
    // on every refresh.
    if (!pulseInitRef.current) {
      for (const r of tx) seenRef.current.add(txFingerprint(r));
      pulseInitRef.current = true;
      return;
    }

    const now = Date.now();
    const LIVE_WINDOW_MS = 60 * 1000;
    let cr = 0,
      db = 0;

    for (const r of tx) {
      const fp = txFingerprint(r);
      const isNew = !seenRef.current.has(fp);
      if (isNew) {
        seenRef.current.add(fp);
        if (
          r.status !== "FAILED" &&
          now - Number(r.ts || 0) <= LIVE_WINDOW_MS
        ) {
          if (r.dir === "CREDIT") cr += r.amt || 0;
          else if (r.dir === "DEBIT") db += r.amt || 0;
        }
      }
    }

    if (cr === 0 && db === 0) return;

    setPulse((prev) => ({
      crCents: (prev.visible ? prev.crCents : 0) + cr,
      dbCents: (prev.visible ? prev.dbCents : 0) + db,
      visible: true,
    }));

    if (hideTimerRef.current) clearTimeout(hideTimerRef.current);
    hideTimerRef.current = setTimeout(
      () => setPulse({ crCents: 0, dbCents: 0, visible: false }),
      LIVE_WINDOW_MS
    );
  }, [tx]);

  useEffect(() => {
    return () => {
      if (hideTimerRef.current) clearTimeout(hideTimerRef.current);
    };
  }, []);

  // draw / update charts
  useEffect(() => {
    const destroy = (key) => {
      if (chartsRef.current[key]) {
        chartsRef.current[key].destroy();
        chartsRef.current[key] = null;
      }
    };
    const draw = (key, ctx, cfg) => {
      destroy(key);
      if (ctx) chartsRef.current[key] = new Chart(ctx, cfg);
    };

    const yTickFmt = (v) => Number(v).toLocaleString("en-US");

    const common = {
      maintainAspectRatio: false,
      responsive: true,
      layout: { padding: 4 },
      plugins: {
        legend: {
          position: "bottom",
          labels: {
            usePointStyle: true,
            boxWidth: 8,
            padding: 10,
            font: { size: 11 },
          },
        },
        tooltip: { mode: "index", intersect: false },
      },
      scales: {
        x: { ticks: { font: { size: 11 } } },
        y: {
          beginAtZero: true,
          ticks: { font: { size: 11 }, callback: yTickFmt },
        },
      },
      elements: { line: { tension: 0.3 }, point: { radius: 2, hitRadius: 6 } },
    };

    const r1 = document.getElementById("chart_range_bars");
    if (r1) {
      draw("rangeBars", r1.getContext("2d"), {
        type: "bar",
        data: {
          labels: ["Today", "This week", "This month", "This year"],
          datasets: [
            {
              label: "Credits (BTN.)",
              data: [
                analytics.totals.today.cr,
                analytics.totals.week.cr,
                analytics.totals.month.cr,
                analytics.totals.year.cr,
              ],
              maxBarThickness: 26,
              categoryPercentage: 0.6,
              barPercentage: 0.7,
            },
            {
              label: "Debits (BTN.)",
              data: [
                analytics.totals.today.db,
                analytics.totals.week.db,
                analytics.totals.month.db,
                analytics.totals.year.db,
              ],
              maxBarThickness: 26,
              categoryPercentage: 0.6,
              barPercentage: 0.7,
            },
          ],
        },
        options: {
          ...common,
          scales: {
            x: {
              ...common.scales.x,
              stacked: false,
              title: { display: true, text: "Period" },
            },
            y: {
              ...common.scales.y,
              stacked: false,
              title: { display: true, text: "Amount (BTN.)" },
            },
          },
        },
      });
    }

    const r2 = document.getElementById("chart_daily_cd");
    if (r2) {
      draw("dailyCD", r2.getContext("2d"), {
        type: "bar",
        data: {
          labels: analytics.daily14.labels,
          datasets: [
            {
              label: "Credits (BTN.)",
              data: analytics.daily14.cr,
              stack: "cd",
              maxBarThickness: 18,
            },
            {
              label: "Debits (BTN.)",
              data: analytics.daily14.db,
              stack: "cd",
              maxBarThickness: 18,
            },
          ],
        },
        options: {
          ...common,
          scales: {
            x: {
              ...common.scales.x,
              stacked: true,
              title: { display: true, text: "Date" },
            },
            y: {
              ...common.scales.y,
              stacked: true,
              title: { display: true, text: "Amount (BTN.)" },
            },
          },
        },
      });
    }

    const r3 = document.getElementById("chart_cd_donut");
    if (r3) {
      draw("cdDonut", r3.getContext("2d"), {
        type: "doughnut",
        data: {
          labels: ["Credits", "Debits"],
          datasets: [
            {
              data: [analytics.filteredBTN.cr, analytics.filteredBTN.db],
              borderWidth: 1,
            },
          ],
        },
        options: {
          maintainAspectRatio: false,
          responsive: true,
          layout: { padding: 4 },
          plugins: {
            legend: {
              position: "bottom",
              labels: {
                usePointStyle: true,
                boxWidth: 8,
                padding: 10,
                font: { size: 11 },
              },
            },
            tooltip: {
              mode: "index",
              intersect: false,
              callbacks: {
                label: (ctx) =>
                  `${ctx.label}: ${formatBTNNumber(ctx.parsed)}`,
              },
            },
          },
          cutout: "62%",
          scales: {},
        },
      });
    }

    const r4 = document.getElementById("chart_balance_line");
    if (r4) {
      draw("balanceLine", r4.getContext("2d"), {
        type: "line",
        data: {
          labels: analytics.balance30.labels,
          datasets: [
            {
              label: "Balance (BTN.)",
              data: analytics.balance30.balances,
              fill: false,
            },
          ],
        },
        options: {
          ...common,
          scales: {
            x: {
              ...common.scales.x,
              title: { display: true, text: "Date" },
            },
            y: {
              ...common.scales.y,
              title: { display: true, text: "Balance (BTN.)" },
            },
          },
          plugins: {
            ...common.plugins,
            tooltip: {
              mode: "index",
              intersect: false,
              callbacks: {
                label: (ctx) =>
                  `${ctx.dataset.label}: ${formatBTNNumber(
                    ctx.parsed.y
                  )}`,
              },
            },
          },
        },
      });
    }

    return () => {
      for (const k of Object.keys(chartsRef.current)) {
        if (chartsRef.current[k]) chartsRef.current[k].destroy();
      }
      chartsRef.current = {};
    };
  }, [analytics]);

  /* ============== UI ============== */

  return (
    <div className="wallet-page container-fluid px-4">
      {/* WALLET SUMMARY (read-only) */}
      <div className="row g-4 mb-3 wallet-cards align-items-stretch">
        <div className="col-12 d-flex">
          <div className="card p-3 wallet-balance-card h-100 flex-fill">
            <div className="d-flex align-items-center gap-2 flex-wrap">
              <h5 className="mb-0 me-2">Current Balance</h5>
              <span
                className={`status-badge ${
                  wallet.status === "ACTIVE" ? "active" : "frozen"
                }`}
              >
                {wallet.status || "ACTIVE"}
              </span>
              {loading && <small className="text-muted">Loading…</small>}
              {apiErr && <small className="text-danger">({apiErr})</small>}
            </div>

            <div className="balance-grid">
              <div className="balance-info">
                <div className="balance-display">
                  {fmt(wallet.balanceCents)}
                </div>

                {/* Credits inflow + totals on the same row */}
                <div className="mini d-flex gap-4 flex-wrap">
                  <div>
                    <div className="mini-label">Today</div>
                    <div className="mini-value">
                      {fmt(inflow.today)}
                    </div>
                  </div>
                  <div>
                    <div className="mini-label">This week</div>
                    <div className="mini-value">
                      {fmt(inflow.week)}
                    </div>
                  </div>
                  <div>
                    <div className="mini-label">This month</div>
                    <div className="mini-value">
                      {fmt(inflow.month)}
                    </div>
                  </div>
                  <div>
                    <div className="mini-label">This year</div>
                    <div className="mini-value">
                      {fmt(inflow.year)}
                    </div>
                  </div>

                  {/* Total credited / debited from DB (or all tx fallback) */}
                  <div>
                    <div className="mini-label">Total credited</div>
                    <div className="mini-value">
                      BTN. {formatBTNNumber(totalCredBTN)}
                    </div>
                  </div>
                  <div>
                    <div className="mini-label">Total debited</div>
                    <div className="mini-value">
                      BTN. {formatBTNNumber(totalDebitBTN)}
                    </div>
                  </div>
                </div>

                <div className="kpi-chips">
                  <div className="chip">
                    <div className="chip-label">Wallet ID</div>
                    <div className="chip-sub">
                      {wallet.wallet_id || "—"}
                    </div>
                  </div>
                  <div className="chip">
                    <div className="chip-label">Admin ID</div>
                    <div className="chip-sub">
                      {wallet.user_id ?? "—"}
                    </div>
                  </div>
                  <div className="chip">
                    <div className="chip-label">Created</div>
                    <div className="chip-sub">
                      {wallet.created_at || "—"}
                    </div>
                  </div>
                  <div className="chip">
                    <div className="chip-label">Updated</div>
                    <div className="chip-sub">
                      {wallet.updated_at || "—"}
                    </div>
                  </div>
                </div>
              </div>

              <div
                className="balance-side"
                style={{
                  flex: 1,
                  display: "flex",
                  alignItems: "flex-start",
                  justifyContent: "flex-end",
                  gap: 8,
                }}
              >
                {pulse.visible && pulse.crCents > 0 && (
                  <div
                    className="pulse-badge credit"
                    style={{
                      display: "inline-flex",
                      alignItems: "center",
                      gap: 8,
                      padding: "6px 10px",
                      borderRadius: 999,
                      fontWeight: 600,
                      background: "rgba(34,197,94,.12)",
                      color: "#15803d",
                    }}
                    aria-live="polite"
                  >
                    <i
                      className="fa fa-arrow-up"
                      aria-hidden="true"
                    />
                    <span>
                      + BTN.{" "}
                      {formatBTNNumber(pulse.crCents / 100)}
                    </span>
                  </div>
                )}
                {pulse.visible && pulse.dbCents > 0 && (
                  <div
                    className="pulse-badge debit"
                    style={{
                      display: "inline-flex",
                      alignItems: "center",
                      gap: 8,
                      padding: "6px 10px",
                      borderRadius: 999,
                      fontWeight: 600,
                      background: "rgba(239,68,68,.12)",
                      color: "#b91c1c",
                    }}
                    aria-live="polite"
                  >
                    <i
                      className="fa fa-arrow-down"
                      aria-hidden="true"
                    />
                    <span>
                      - BTN.{" "}
                      {formatBTNNumber(pulse.dbCents / 100)}
                    </span>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* TIPS & REWARDS */}
      <div className="row g-4 mb-3">
        <div className="col-12 d-flex">
          <div className="card p-3 wallet-rewards-card flex-fill">
            <h5 className="rewards-title">Reward</h5>

            <div
              className="rewards-strip"
              aria-label="Tips deposit to a public user"
            >
              <div className="field">
                <label className="form-label">
                  Wallet ID (USERS)
                </label>
                <input
                  type="text"
                  className="filter-input"
                  placeholder=" "
                  value={tipWalletId}
                  onChange={(e) =>
                    setTipWalletId(e.target.value.trim())
                  }
                />
              </div>

              <div className="field">
                <label className="form-label">
                  Amount (BTN)
                </label>
                <input
                  type="text"
                  inputMode="decimal"
                  className="filter-input"
                  value={FIXED_REWARD_AMOUNT_TEXT}
                  readOnly
                  aria-readonly="true"
                  onWheel={(e) => e.currentTarget.blur()}
                />
              </div>

              <div
                className="field"
                style={{ minWidth: 260 }}
              >
                <label className="form-label d-flex justify-content-between align-items-center">
                  <span>Remarks</span>
                  <small className="text-muted">
                    {tipRemark.length}/{remarkMax}
                  </small>
                </label>
                <input
                  type="text"
                  className="filter-input"
                  maxLength={remarkMax}
                  value={tipRemark}
                  onChange={(e) =>
                    setTipRemark(e.target.value.replace(/\n/g, ""))
                  }
                />
              </div>

              <div className="stat-chip">
                <div className="stat-label">Total payout</div>
                <div className="stat-value">
                  {fmt(totalPayoutCents)}
                </div>
              </div>
              <div className="d-flex gap-2 align-items-center flex-wrap">
                <button
                  className="btn btn-primary stat-button"
                  disabled={
                    !ACTIONS_ENABLED ||
                    tipBusy ||
                    tpinBusy ||
                    tpinCheckBusy ||
                    !walletUnlocked ||
                    !hasTPin ||
                    !tipWalletId ||
                    totalPayoutCents <= 0 ||
                    (selectedRewardMeta.existsInEligibleTable &&
                      selectedRewardMeta.isCoolingDown)
                  }
                  title={
                    ACTIONS_ENABLED
                      ? !walletUnlocked || !hasTPin
                        ? "Set up wallet T-PIN first"
                        : !tipWalletId
                        ? "Enter recipient wallet ID"
                        : selectedRewardMeta.existsInEligibleTable &&
                          selectedRewardMeta.isCoolingDown
                        ? `Reward already paid. Next eligible on ${new Date(
                            selectedRewardMeta.nextEligibleTs
                          ).toLocaleString()}.`
                        : ""
                      : "Disabled on this admin build (read-only)."
                  }
                  onClick={handleDepositTips}
                >
                  {tipBusy ? "Processing…" : tpinCheckBusy ? "Checking…" : "Deposit now"}
                </button>
              </div>
            </div>

            <div className="mt-2 small text-success" role="status" aria-live="polite">
              Reward payout is open anytime for eligible drivers, but the same driver can receive one special reward only once every 6 months.
            </div>

            {tipWalletId && selectedRewardMeta.existsInEligibleTable && (
              <div
                className={`mt-2 small ${
                  selectedRewardMeta.isCoolingDown ? "text-warning" : "text-muted"
                }`}
                role="status"
                aria-live="polite"
              >
                {selectedRewardMeta.lastPaidTs > 0
                  ? selectedRewardMeta.isCoolingDown
                    ? `This driver already received a special reward on ${new Date(
                        selectedRewardMeta.lastPaidTs
                      ).toLocaleString()}. Next eligible date is ${new Date(
                        selectedRewardMeta.nextEligibleTs
                      ).toLocaleString()}.`
                    : `This driver last received a special reward on ${new Date(
                        selectedRewardMeta.lastPaidTs
                      ).toLocaleString()} and can be paid again now.`
                  : "This driver has not received any special reward yet and can be paid now."}
              </div>
            )}


            {tpinCheckErr && (
              <div className="mt-2 small text-danger" role="status" aria-live="polite">
                {tpinCheckErr}
              </div>
            )}

            {tipMsg.text && (
              <div
                className={`mt-2 small ${
                  tipMsg.kind === "ok"
                    ? "text-success"
                    : "text-danger"
                }`}
                role="status"
                aria-live="polite"
              >
                {tipMsg.text}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* ✅ Average Rated Drivers ONLY */}
      <div className="row g-4 mb-3">
        <div className="col-12 d-flex">
          <div className="card p-3 flex-fill">
            <div className="d-flex justify-content-between align-items-center mb-2">
              <h5 className="mb-0">Average Rated Drivers</h5>
            </div>

            {avgRatedErr && (
              <div className="text-danger small mb-2">
                ({avgRatedErr})
              </div>
            )}

            <div className="d-flex flex-wrap gap-2 mb-3">
              <div className="stat-chip" style={{ minWidth: 210 }}>
                <div className="stat-label">
                  Paid drivers • All time
                </div>
                <div className="stat-value">
                  {rewardPaymentSummary.currentCycle.paidWalletIds.size} / {avgRatedRows.length}
                </div>
              </div>
              <div className="stat-chip" style={{ minWidth: 210 }}>
                <div className="stat-label">
                  Total paid • All time
                </div>
                <div className="stat-value">
                  {fmt(rewardPaymentSummary.currentCycle.totalCents)}
                </div>
              </div>
              <div className="stat-chip" style={{ minWidth: 210 }}>
                <div className="stat-label">
                  Eligible now • 6 month rule
                </div>
                <div className="stat-value">
                  {rewardPaymentSummary.eligibleNowCount}
                </div>
              </div>
              <div className="stat-chip" style={{ minWidth: 210 }}>
                <div className="stat-label">
                  Reward status • 6 month rule
                </div>
                <div className="stat-value">
                  Open now
                </div>
              </div>
              <div className="stat-chip" style={{ minWidth: 210 }}>
                <div className="stat-label">
                  Paid drivers • {rewardPaymentSummary.yearLabel}
                </div>
                <div className="stat-value">
                  {rewardPaymentSummary.year.paidWalletIds.size}
                </div>
              </div>
              <div className="stat-chip" style={{ minWidth: 210 }}>
                <div className="stat-label">
                  Total paid • {rewardPaymentSummary.yearLabel}
                </div>
                <div className="stat-value">
                  {fmt(rewardPaymentSummary.year.totalCents)}
                </div>
              </div>
            </div>

            <div className="small text-muted mb-2">
              Special rewards can be paid anytime to eligible drivers, but the same driver can receive only one special reward every 6 months.
            </div>

            <div className="table-card">
              <div className="table-wrap">
                <table className="table" style={tableStyle}>
                  <colgroup>
                    <col style={{ width: 80 }} />
                    <col style={{ width: 140 }} />
                    <col style={{ width: 120 }} />
                    <col style={{ width: 220 }} />
                    <col style={{ width: 160 }} />
                    <col style={{ width: 150 }} />
                    <col style={{ width: 150 }} />
                    <col style={{ width: 180 }} />
                    <col style={{ width: 180 }} />
                    <col style={{ width: 260 }} />
                    <col style={{ width: 160 }} />
                    <col style={{ width: 310 }} />
                    <col style={{ width: 180 }} />
                  </colgroup>

                  <thead>
                    <tr>
                      <th>SI.No</th>
                      <th>Type</th>
                      <th>Image</th>
                      <th>Name</th>
                      <th>Wallet ID</th>
                      <th>Deposit Status</th>
                      <th>Total Paid</th>
                      <th>Last Paid</th>
                      <th>Eligible Now</th>
                      <th>Email</th>
                      <th>Phone</th>
                      <th>Vehicle</th>
                      <th>Avg Rating</th>
                    </tr>
                  </thead>

                  <tbody>
                    {avgRatedLoading ? (
                      <tr>
                        <td colSpan={13} className="text-muted">
                          Loading…
                        </td>
                      </tr>
                    ) : avgRatedRows.length ? (
                      paginatedAvgRatedRows.map((r, idx) => {
                        const name =
                          r.user_name?.trim() ||
                          r.name?.trim() ||
                          "-";
                        const driverWalletId = getWalletId(r);
                        const driverWalletKey = normalizeWalletKey(driverWalletId);
                        const totalPaidCents = Number(
                          rewardPaymentSummary.currentCycle.amountByWallet.get(driverWalletKey) || 0
                        );
                        const lastPaidTs = Number(
                          rewardPaymentSummary.lastPaidAtByWallet.get(driverWalletKey) || 0
                        );
                        const nextEligibleTs = Number(
                          rewardPaymentSummary.nextEligibleAtByWallet.get(driverWalletKey) || 0
                        );
                        const isCoolingDown =
                          rewardPaymentSummary.cooldown.lockedWalletIds.has(driverWalletKey);
                        const depositStatusText = isCoolingDown ? "Cooling down" : "Available";
                        const depositStatusStyle = isCoolingDown
                          ? {
                              background: "rgba(245, 158, 11, 0.12)",
                              color: "#b45309",
                            }
                          : {
                              background: "rgba(59, 130, 246, 0.12)",
                              color: "#1d4ed8",
                            };
                        return (
                          <tr
                            key={`Driver-${r.user_id ?? r.id ?? idx}`}
                          >
                            <td
                              style={{
                                whiteSpace: "nowrap",
                              }}
                            >
                              {(avgRatedPage - 1) * AVG_RATED_PAGE_SIZE + idx + 1}
                            </td>
                            <td>
                              <span className="type-pill">
                                Driver
                              </span>
                            </td>
                            <td>
                              <Avatar
                                src={r.profile_image}
                                name={name}
                              />
                            </td>
                            <td className="cell-wrap">
                              {name}
                            </td>
                            <td
                              style={monoEllip}
                              title={driverWalletId}
                              className="mono"
                            >
                              {driverWalletId}
                            </td>
                            <td>
                              <span
                                style={{
                                  display: "inline-flex",
                                  alignItems: "center",
                                  justifyContent: "center",
                                  minWidth: 78,
                                  padding: "4px 10px",
                                  borderRadius: 999,
                                  fontSize: 12,
                                  fontWeight: 700,
                                  ...depositStatusStyle,
                                }}
                              >
                                {depositStatusText}
                              </span>
                            </td>
                            <td
                              style={{
                                whiteSpace: "nowrap",
                              }}
                              className="mono"
                            >
                              {totalPaidCents > 0 ? fmt(totalPaidCents) : "—"}
                            </td>
                            <td
                              style={{
                                whiteSpace: "nowrap",
                              }}
                            >
                              {lastPaidTs > 0
                                ? new Date(lastPaidTs).toLocaleString()
                                : "—"}
                            </td>
                            <td
                              style={{
                                whiteSpace: "nowrap",
                              }}
                            >
                              {isCoolingDown && nextEligibleTs > 0
                                ? new Date(nextEligibleTs).toLocaleDateString()
                                : "Now"}
                            </td>
                            <td className="cell-wrap">
                              {r.email || "—"}
                            </td>
                            <td
                              style={{
                                whiteSpace: "nowrap",
                              }}
                            >
                              {r.phone || "—"}
                            </td>
                            <td className="cell-wrap">
                              {r.extra || "—"}
                            </td>
                            <td className="mono">
                              {getAverageRating(r)}
                            </td>
                          </tr>
                        );
                      })
                    ) : (
                      <tr>
                        <td colSpan={13} className="text-muted">
                          No drivers with avg rating above 4.5.
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>

            <div
              className="mt-3"
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                gap: 16,
                flexWrap: "wrap",
              }}
            >
              <small className="text-muted" style={{ margin: 0 }}>
                Showing {avgRatedRows.length ? (avgRatedPage - 1) * AVG_RATED_PAGE_SIZE + 1 : 0}
                -{Math.min(avgRatedPage * AVG_RATED_PAGE_SIZE, avgRatedVisibleCount)} of {avgRatedVisibleCount} drivers
                {avgRatedRows.length > avgRatedVisibleCount ? " (first 100 pages only)" : ""}
              </small>

              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "flex-end",
                  gap: 10,
                  flexWrap: "wrap",
                  marginLeft: "auto",
                }}
              >
                <button
                  type="button"
                  className="btn btn-outline-secondary btn-sm"
                  style={{
                    width: "auto",
                    minWidth: 96,
                    padding: "8px 14px",
                    borderRadius: 999,
                    display: "inline-flex",
                    alignItems: "center",
                    justifyContent: "center",
                    flex: "0 0 auto",
                    whiteSpace: "nowrap",
                    lineHeight: 1.2,
                  }}
                  disabled={avgRatedPage <= 1}
                  onClick={() => setAvgRatedPage((prev) => Math.max(1, prev - 1))}
                >
                  Previous
                </button>

                <span
                  className="small text-muted"
                  style={{ whiteSpace: "nowrap", margin: 0 }}
                >
                  Page {Math.min(avgRatedPage, totalAvgRatedPages)} of {totalAvgRatedPages}
                </span>

                <button
                  type="button"
                  className="btn btn-outline-secondary btn-sm"
                  style={{
                    width: "auto",
                    minWidth: 96,
                    padding: "8px 14px",
                    borderRadius: 999,
                    display: "inline-flex",
                    alignItems: "center",
                    justifyContent: "center",
                    flex: "0 0 auto",
                    whiteSpace: "nowrap",
                    lineHeight: 1.2,
                  }}
                  disabled={avgRatedPage >= totalAvgRatedPages}
                  onClick={() => setAvgRatedPage((prev) => Math.min(totalAvgRatedPages, prev + 1))}
                >
                  Next
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* ========= ANALYTICS VISUALS (COMPACT) ========= */}
      <div className="row g-4 mb-3">
        <div className="col-12 col-lg-6 d-flex">
          <div className="card p-3 flex-fill chart-card">
            <h5 className="mb-2">
              Credits vs Debits ( Today - Week - Month -
              Year)
            </h5>
            <div className="chart-box">
              <canvas
                id="chart_range_bars"
                aria-label="Period credits vs debits"
              />
            </div>
          </div>
        </div>

        <div className="col-12 col-lg-6 d-flex">
          <div className="card p-3 flex-fill chart-card">
            <h5 className="mb-2">Credits vs Debits</h5>
            <div className="chart-box sm">
              <canvas
                id="chart_cd_donut"
                aria-label="Credits vs Debits donut"
              />
            </div>
          </div>
        </div>

        <div className="col-12 d-flex">
          <div className="card p-3 flex-fill chart-card">
            <h5 className="mb-2">
              Daily Credits & Debits - Last 14 days
            </h5>
            <div className="chart-box">
              <canvas
                id="chart_daily_cd"
                aria-label="Daily credits/debits"
              />
            </div>
          </div>
        </div>

        <div className="col-12 d-flex">
          <div className="card p-3 flex-fill chart-card">
            <h5 className="mb-2">
              Balance Trend - Last 30 days
            </h5>
            <div className="chart-box">
              <canvas
                id="chart_balance_line"
                aria-label="Balance trend"
              />
            </div>
            <small className="text-muted">
              Trend reconstructed from current balance and
              daily net activity.
            </small>
          </div>
        </div>
      </div>

      {/* TRANSACTIONS */}
      <div className="wallet-toolbar mb-2">
        <h5 className="toolbar-title">
          Transactions{" "}
          {txLoading && (
            <small className="text-muted">(loading…)</small>
          )}
          {txErr && (
            <small className="text-danger"> ({txErr})</small>
          )}
          {!TX_CURSOR_API && !TX_BASE && (
            <small className="text-muted">
              {" "}
              (No TX API configured)
            </small>
          )}
        </h5>

        <div className="toolbar-strip">
          <div className="filters-wrap">
            <div
              className="input-with-icon"
              style={{ minWidth: 420 }}
            >
              <i
                className="fa fa-search"
                aria-hidden="true"
              />
              <input
                className="filter-input"
                placeholder="Search journal number, reference, wallet IDs, or remarks"
                value={q}
                onChange={(e) =>
                  setQ(e.target.value)
                }
              />
            </div>

            <input
              type="date"
              className="filter-input date"
              value={startDate}
              onChange={(e) =>
                setStartDate(e.target.value)
              }
              aria-label="Start date"
            />
            <span className="to-word">to</span>
            <input
              type="date"
              className="filter-input date"
              value={endDate}
              onChange={(e) =>
                setEndDate(e.target.value)
              }
              aria-label="End date"
            />
          </div>

          <div className="export-group">
            <button
              className="btn btn-outline-success"
              onClick={exportExcel}
              disabled={!rows.length}
            >
              Export Excel
            </button>
          </div>
        </div>
      </div>

      {/* TABLE (deduped; only latest 10 shown) */}
      <div className="table-card">
        <div className="table-wrap">
          <table className="table" style={tableStyle}>
            <colgroup>
              <col style={{ width: 64 }} />
              <col style={{ width: 190 }} />
              <col style={{ width: 180 }} />
              <col style={{ width: 180 }} />
              <col style={{ width: 200 }} />
              <col style={{ width: 110 }} />
              <col style={{ width: 150 }} />
              <col style={{ width: 230 }} />
              <col style={{ width: 280 }} />
              <col style={{ width: 110 }} />
            </colgroup>

            <thead>
              <tr>
                <th>SI.No</th>
                <th>Date</th>
                <th>Journal number</th>
                <th>Admin Wallet ID</th>
                <th>Users Wallet ID</th>
                <th>Direction</th>
                <th>Amount</th>
                <th>Reference</th>
                <th>Remarks</th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
              {displayRows.length ? (
                displayRows.map((r, idx) => (
                  <tr
                    key={`${txFingerprint(r)}:${idx}`}
                  >
                    <td
                      style={{
                        whiteSpace: "nowrap",
                      }}
                    >
                      {idx + 1}
                    </td>
                    <td
                      style={{
                        whiteSpace: "nowrap",
                      }}
                    >
                      {new Date(r.ts).toLocaleString()}
                    </td>
                    <td
                      style={{
                        whiteSpace: "nowrap",
                      }}
                    >
                      {r.journal || "—"}
                    </td>
                    <td
                      style={monoEllip}
                      title={r.admin_wallet_id || ""}
                    >
                      {r.admin_wallet_id || "—"}
                    </td>
                    <td
                      style={monoEllip}
                      title={r.user_wallet_id || ""}
                    >
                      {r.user_wallet_id || "—"}
                    </td>
                    <td>{r.dir}</td>
                    <td
                      className="amount"
                      style={{
                        whiteSpace: "nowrap",
                      }}
                    >
                      {fmt(r.amt)}
                    </td>
                    <td
                      style={monoEllip}
                      title={r.ref || ""}
                    >
                      {r.ref || "—"}
                    </td>
                    <td
                      style={remarksStyle}
                      title={r.note || ""}
                    >
                      {r.note || "—"}
                    </td>
                    <td
                      style={{
                        whiteSpace: "nowrap",
                      }}
                    >
                      {r.status}
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td
                    colSpan={10}
                    className="text-muted"
                  >
                    {txLoading
                      ? "Loading…"
                      : "No transactions found."}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      <footer className="dashboard-footer mt-4">
        <small>
          © {new Date().getFullYear()}{" "}
          <strong>སྟབས་བདེ། </strong> Admin • All
          rights reserved
        </small>
      </footer>
    </div>
  );
}
